import StyledInput from '@/components/styled/StyledInput';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DOUBLE_ROOM_COMBINATIONS,
  INDIVIDUAL_ROOM_COMBINATIONS,
  QUADRUPLE_ROOM_COMBINATIONS,
  TRIPLE_ROOM_COMBINATIONS,
} from '@/constants/HotelRoomTypes';
import { HotelFormData } from '@/types/benefits/hotel/HotelFormData';
import { getHotelRoomRepartitionLabel } from '@/utils/benefits/getHotelRoomRepartitionLabel';
import {
  hotelPricePartitionArraysEqual,
  mergeHotelPricePartitionsByRoomOptions,
} from '@/utils/benefits/hotel/mergeHotelPricePartitionsByRoomOptions';
import { useEffect, useMemo, useRef } from 'react';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from '@/i18n';
import ExpertLineBadge from '../excelPaste/ExpertLineBadge';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface HotelRoomPriceRepartitionListProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  tarifIndex: number;
  tarifFieldId?: string;
  pricingType?: 'byPerson' | 'byRoom';
  reducedPriceExists?: 'true' | 'false';
  priceReductionType?: 'newPrice' | 'reduction';
  isViewMode?: boolean;
}

const individualPartitionsCache = new Map<
  string,
  HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['individual']
>();

export const IndividualHotelRoomPriceRepartitionList = ({
  control,
  index,
  tarifIndex,
  tarifFieldId,
  pricingType,
  reducedPriceExists,
  priceReductionType,
  isViewMode = false,
}: HotelRoomPriceRepartitionListProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<HotelFormDataWithTranslations>();
  const lastMergedRoomOptionsRef = useRef<number[] | null>(null);
  const lastKnownNonEmptyPartitionsRef = useRef<
    HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['individual']
  >([]);

  const { fields, replace } = useFieldArray({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.individual`,
  });

  const individualRoomType = useWatch({
    control,
    name: `hotelRoomCategories.${index}.roomTypes.individual`,
  });

  const individualRoomOptions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.individualRoomOptions`,
  });

  const childAges = useWatch({ control, name: 'childAges' });

  const defaultPricesExist = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPricesExist`,
  });

  const defaultPrices = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices`,
  });

  const defaultValues = useMemo(
    () => ({
      price:
        pricingType === 'byRoom' &&
        defaultPricesExist === 'true' &&
        defaultPrices?.price
          ? Number(defaultPrices?.price)
          : undefined,
    }),
    [pricingType, defaultPricesExist, defaultPrices?.price],
  );

  useEffect(() => {
    const path =
      `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.individual` as const;
    const opts = individualRoomOptions ?? [];
    const cacheKey = `${index}:${tarifFieldId ?? tarifIndex}`;

    if (!individualRoomType) {
      replace([]);
      lastMergedRoomOptionsRef.current = null;
      return;
    }

    const parts = (getValues(path) ??
      []) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['individual'];
    if (parts.some((p) => p.price !== undefined)) {
      lastKnownNonEmptyPartitionsRef.current = parts.map((p) => ({
        ...p,
      }));
      individualPartitionsCache.set(
        cacheKey,
        parts.map((p) => ({ ...p })),
      );
    }
    const previousRef = lastMergedRoomOptionsRef.current;
    const prevOpts = previousRef ?? opts;
    const cachedParts =
      lastKnownNonEmptyPartitionsRef.current.length > 0
        ? lastKnownNonEmptyPartitionsRef.current
        : (individualPartitionsCache.get(cacheKey) ?? []);
    const shouldRestoreFromCache =
      previousRef === null &&
      parts.length === 0 &&
      cachedParts.length > 0 &&
      opts.length > 0;
    const sourceParts = shouldRestoreFromCache ? cachedParts : parts;

    const merged = mergeHotelPricePartitionsByRoomOptions({
      previousRoomOptionIndices: prevOpts,
      previousPartitions: sourceParts,
      nextRoomOptionIndices: opts,
      createDefaultPartition: () =>
        ({
          price: defaultValues.price,
        }) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['individual'][number],
    });
    if (shouldRestoreFromCache) {
      // Restore previous values when re-enabling individual options.
    }

    const optionsDidNotChange =
      prevOpts.length === opts.length &&
      prevOpts.every((value, i) => value === opts[i]);
    if (parts.length === 0 && optionsDidNotChange && previousRef !== null) {
      return;
    }

    if (hotelPricePartitionArraysEqual(merged, parts)) {
      lastMergedRoomOptionsRef.current = [...opts];
      return;
    }

    replace(merged);
    lastMergedRoomOptionsRef.current = [...opts];
  }, [
    defaultValues.price,
    getValues,
    index,
    individualRoomOptions,
    individualRoomType,
    replace,
    tarifFieldId,
    tarifIndex,
  ]);

  const individualPricePartition = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.individual`,
  });

  const total = useMemo(() => {
    if (individualPricePartition && individualPricePartition.length > 0) {
      return (
        +(individualPricePartition[0].price ?? 0) +
        +(pricingType === 'byPerson' &&
        reducedPriceExists === 'true' &&
        priceReductionType === 'reduction'
          ? (individualPricePartition[0].additionalPrice ?? 0)
          : 0)
      );
    } else return 0;
  }, [
    individualPricePartition,
    priceReductionType,
    pricingType,
    reducedPriceExists,
  ]);

  return fields && fields.length ? (
    <div className="flex flex-col gap-4">
      {fields.map((field, indexField) => (
        <table key={field.id}>
          <caption className="font-semibold rounded-t-lg pt-2 bg-[#F7F7F7]">
            <ExpertLineBadge
              roomType="individual"
              optionIndex={individualRoomOptions?.[indexField]}
            />
            {t('hotels.roomTarifs.individual')}
          </caption>
          <thead className="rounded-b-lg bg-[#F7F7F7]">
            <tr>
              <th className="pl-1 pb-2">
                <img src="/images/single_bedroom.png" className="size-10" />
              </th>
              <th className="text-left w-36">
                {getHotelRoomRepartitionLabel(
                  INDIVIDUAL_ROOM_COMBINATIONS[
                    individualRoomOptions[indexField]
                  ],
                  childAges ?? [],
                )}
              </th>
              <th className="text-left w-36"></th>
              <th className="text-left w-36"></th>
              <th colSpan={2}></th>
            </tr>
          </thead>
          <tbody>
            <tr className="align-top">
              <td></td>
              <td>
                <div className="w-28">
                  <StyledInput
                    control={control}
                    name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.individual.${indexField}.price`}
                    type="number"
                    min={0}
                    title={
                      pricingType === 'byRoom'
                        ? t('hotels.roomTarifs.totalRoom')
                        : t('hotels.roomTarifs.adultRate')
                    }
                    className="items-center"
                    disabled={isViewMode}
                    hideNumberFieldArrows
                    compareValue={
                      pricingType === 'byRoom' &&
                      defaultPricesExist === 'true' &&
                      defaultValues.price !== undefined
                        ? {
                            color: 'purple',
                            value: defaultValues?.price?.toString(),
                          }
                        : undefined
                    }
                  />
                </div>
              </td>
              <td>
                {pricingType === 'byPerson' &&
                  reducedPriceExists === 'true' &&
                  priceReductionType === 'reduction' && (
                    <div className="w-24">
                      <StyledInput
                        control={control}
                        name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.individual.${indexField}.additionalPrice`}
                        type="number"
                        min={0}
                        title={t('hotels.roomTarifs.singleSupplement')}
                        titleClassName="text-warning-700"
                        className="items-center"
                        disabled={isViewMode}
                        hideNumberFieldArrows
                      />
                    </div>
                  )}
              </td>
              <td></td>
              <td>
                {pricingType === 'byPerson' && (
                  <div className="w-24">
                    <div className="flex flex-col w-full gap-1.5 items-center">
                      <div className="flex justify-between">
                        <Label className="text-gray-700">
                          {t('hotels.roomTarifs.totalRoomLabel')}
                        </Label>
                      </div>
                      <div className="relative flex items-center">
                        <Input
                          value={total}
                          disabled
                          type="number"
                          className="bg-gray-100 hover:cursor-default [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      <span className="text-sm leading-6 font-medium text-error-500"></span>
                    </div>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      ))}
    </div>
  ) : null;
};

export const DoubleHotelRoomPriceRepartitionList = ({
  control,
  index,
  tarifIndex,
  pricingType,
  reducedPriceExists,
  priceReductionType,
  isViewMode = false,
}: HotelRoomPriceRepartitionListProps) => {
  const { getValues } = useFormContext<HotelFormDataWithTranslations>();
  const lastMergedRoomOptionsRef = useRef<number[] | null>(null);

  const { fields, replace } = useFieldArray({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.double`,
  });

  const doubleRoomType = useWatch({
    control,
    name: `hotelRoomCategories.${index}.roomTypes.double`,
  });

  const doubleRoomOptions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.doubleRoomOptions`,
  });

  const childAges = useWatch({ control, name: 'childAges' });

  const defaultPricesExist = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPricesExist`,
  });

  const defaultPrices = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices`,
  });

  const defaultValues = useMemo(
    () => ({
      price:
        defaultPricesExist === 'true' && defaultPrices?.price
          ? Number(defaultPrices?.price)
          : undefined,
      firstSpecialPrice:
        defaultPricesExist === 'true' &&
        pricingType === 'byPerson' &&
        defaultPrices?.firstSpecialPrice
          ? Number(defaultPrices?.firstSpecialPrice)
          : undefined,
      secondSpecialPrice:
        defaultPricesExist === 'true' &&
        pricingType === 'byPerson' &&
        defaultPrices?.secondSpecialPrice
          ? Number(defaultPrices?.secondSpecialPrice)
          : undefined,
    }),
    [
      defaultPrices?.firstSpecialPrice,
      defaultPrices?.price,
      defaultPrices?.secondSpecialPrice,
      defaultPricesExist,
      pricingType,
    ],
  );

  useEffect(() => {
    const path =
      `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.double` as const;
    const opts = doubleRoomOptions ?? [];

    if (!doubleRoomType) {
      replace([]);
      lastMergedRoomOptionsRef.current = null;
      return;
    }

    const parts = (getValues(path) ??
      []) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['double'];
    const previousRef = lastMergedRoomOptionsRef.current;
    const prevOpts = previousRef ?? opts;

    const merged = mergeHotelPricePartitionsByRoomOptions({
      previousRoomOptionIndices: prevOpts,
      previousPartitions: parts,
      nextRoomOptionIndices: opts,
      createDefaultPartition: () =>
        ({
          price: defaultValues.price,
          firstSpecialPrice: defaultValues.firstSpecialPrice,
          secondSpecialPrice: defaultValues.secondSpecialPrice,
        }) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['double'][number],
    });

    const optionsDidNotChange =
      prevOpts.length === opts.length &&
      prevOpts.every((value, i) => value === opts[i]);
    if (parts.length === 0 && optionsDidNotChange && previousRef !== null) {
      return;
    }

    if (hotelPricePartitionArraysEqual(merged, parts)) {
      lastMergedRoomOptionsRef.current = [...opts];
      return;
    }

    replace(merged);
    lastMergedRoomOptionsRef.current = [...opts];
  }, [
    defaultValues.firstSpecialPrice,
    defaultValues.price,
    defaultValues.secondSpecialPrice,
    doubleRoomOptions,
    doubleRoomType,
    getValues,
    index,
    replace,
    tarifIndex,
  ]);

  const doublePricePartitions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.double`,
  });

  const totals = useMemo(() => {
    if (doublePricePartitions) {
      return doublePricePartitions.map((partition, indexPartition) => {
        if (!partition.price) return 0;
        if (pricingType === 'byRoom') return 2 * partition.price;
        else if (pricingType === 'byPerson') {
          if (reducedPriceExists === 'false') {
            return 2 * partition.price;
          } else if (reducedPriceExists === 'true') {
            if (priceReductionType === 'newPrice') {
              let price = 0;
              const currentPartition =
                DOUBLE_ROOM_COMBINATIONS[doubleRoomOptions[indexPartition]];
              if (!currentPartition || currentPartition.length === 0) return 0;
              price = currentPartition[0] * partition.price;
              if (currentPartition[1] > 0) {
                price +=
                  currentPartition[1] * (partition.firstSpecialPrice ?? 0);
              }
              if (currentPartition[2] > 0) {
                price +=
                  currentPartition[2] * (partition.secondSpecialPrice ?? 0);
              }
              return price;
            } else if (priceReductionType === 'reduction') {
              const currentPartition =
                DOUBLE_ROOM_COMBINATIONS[doubleRoomOptions[indexPartition]];
              if (!currentPartition || currentPartition.length === 0) return 0;
              let price = 2 * partition.price;
              if (currentPartition[1] > 0) {
                price -=
                  currentPartition[1] * (partition.firstSpecialPrice ?? 0);
              }
              if (currentPartition[2] > 0) {
                price -=
                  currentPartition[2] * (partition.secondSpecialPrice ?? 0);
              }
              return price;
            }
          }
        }
        return 0;
      });
    } else return [];
  }, [
    doublePricePartitions,
    doubleRoomOptions,
    priceReductionType,
    pricingType,
    reducedPriceExists,
  ]);

  return fields && fields.length ? (
    <div className="flex flex-col gap-4">
      {fields.map((field, indexField) => (
        <HotelRoomRepartitionForm
          key={field.id}
          control={control}
          index={index}
          tarifIndex={tarifIndex}
          indexField={indexField}
          pricingType={pricingType}
          reducedPriceExists={reducedPriceExists}
          priceReductionType={priceReductionType}
          childAges={childAges}
          combination={DOUBLE_ROOM_COMBINATIONS[doubleRoomOptions[indexField]]}
          totals={totals}
          type="double"
          isViewMode={isViewMode}
          showTotal={pricingType === 'byPerson'}
          defaultPrices={{
            defaultAdultPrice: defaultValues.price,
            defaultFirstSpecialPrice: defaultValues.firstSpecialPrice,
            defaultSecondSpecialPrice: defaultValues.secondSpecialPrice,
          }}
        />
      ))}
    </div>
  ) : null;
};

export const TripleHotelRoomPriceRepartitionList = ({
  control,
  index,
  tarifIndex,
  pricingType,
  reducedPriceExists,
  priceReductionType,
  isViewMode = false,
}: HotelRoomPriceRepartitionListProps) => {
  const { getValues } = useFormContext<HotelFormDataWithTranslations>();
  const lastMergedRoomOptionsRef = useRef<number[] | null>(null);

  const { fields, replace } = useFieldArray({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.triple`,
  });

  const tripleRoomType = useWatch({
    control,
    name: `hotelRoomCategories.${index}.roomTypes.triple`,
  });

  const tripleRoomOptions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tripleRoomOptions`,
  });

  const childAges = useWatch({ control, name: 'childAges' });

  const defaultPricesExist = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPricesExist`,
  });

  const defaultPrices = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices`,
  });

  const defaultValues = useMemo(
    () => ({
      price:
        defaultPricesExist === 'true' && defaultPrices?.price
          ? Number(defaultPrices?.price)
          : undefined,
      firstSpecialPrice:
        defaultPricesExist === 'true' &&
        pricingType === 'byPerson' &&
        defaultPrices?.firstSpecialPrice
          ? Number(defaultPrices?.firstSpecialPrice)
          : undefined,
      secondSpecialPrice:
        defaultPricesExist === 'true' &&
        pricingType === 'byPerson' &&
        defaultPrices?.secondSpecialPrice
          ? Number(defaultPrices?.secondSpecialPrice)
          : undefined,
    }),
    [
      defaultPrices?.firstSpecialPrice,
      defaultPrices?.price,
      defaultPrices?.secondSpecialPrice,
      defaultPricesExist,
      pricingType,
    ],
  );

  useEffect(() => {
    const path =
      `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.triple` as const;
    const opts = tripleRoomOptions ?? [];

    if (!tripleRoomType) {
      replace([]);
      lastMergedRoomOptionsRef.current = null;
      return;
    }

    const parts = (getValues(path) ??
      []) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['triple'];
    const previousRef = lastMergedRoomOptionsRef.current;
    const prevOpts = previousRef ?? opts;

    const merged = mergeHotelPricePartitionsByRoomOptions({
      previousRoomOptionIndices: prevOpts,
      previousPartitions: parts,
      nextRoomOptionIndices: opts,
      createDefaultPartition: () =>
        ({
          price: defaultValues.price,
          firstSpecialPrice: defaultValues.firstSpecialPrice,
          secondSpecialPrice: defaultValues.secondSpecialPrice,
        }) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['triple'][number],
    });

    const optionsDidNotChange =
      prevOpts.length === opts.length &&
      prevOpts.every((value, i) => value === opts[i]);
    if (parts.length === 0 && optionsDidNotChange && previousRef !== null) {
      return;
    }

    if (hotelPricePartitionArraysEqual(merged, parts)) {
      lastMergedRoomOptionsRef.current = [...opts];
      return;
    }

    replace(merged);
    lastMergedRoomOptionsRef.current = [...opts];
  }, [
    defaultValues.firstSpecialPrice,
    defaultValues.price,
    defaultValues.secondSpecialPrice,
    getValues,
    index,
    replace,
    tarifIndex,
    tripleRoomOptions,
    tripleRoomType,
  ]);

  const triplePricePartitions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.triple`,
  });

  const totals = useMemo(() => {
    if (triplePricePartitions) {
      return triplePricePartitions.map((partition, indexPartition) => {
        if (!partition.price) return 0;
        if (pricingType === 'byRoom') return 3 * partition.price;
        else if (pricingType === 'byPerson') {
          if (reducedPriceExists === 'false') {
            return 3 * partition.price;
          } else if (reducedPriceExists === 'true') {
            if (priceReductionType === 'newPrice') {
              let price = 0;
              const currentPartition =
                TRIPLE_ROOM_COMBINATIONS[tripleRoomOptions[indexPartition]];
              if (!currentPartition || currentPartition.length === 0) return 0;
              price = currentPartition[0] * partition.price;
              if (currentPartition[1] > 0) {
                price +=
                  currentPartition[1] * (partition.firstSpecialPrice ?? 0);
              }
              if (currentPartition[2] > 0) {
                price +=
                  currentPartition[2] * (partition.secondSpecialPrice ?? 0);
              }
              return price;
            } else if (priceReductionType === 'reduction') {
              const currentPartition =
                TRIPLE_ROOM_COMBINATIONS[tripleRoomOptions[indexPartition]];
              if (!currentPartition || currentPartition.length === 0) return 0;
              let price = 3 * partition.price;
              if (currentPartition && currentPartition[1] > 0) {
                price -=
                  currentPartition[1] * (partition.firstSpecialPrice ?? 0);
              }
              if (currentPartition && currentPartition[2] > 0) {
                price -=
                  currentPartition[2] * (partition.secondSpecialPrice ?? 0);
              }
              return price;
            }
          }
        }
        return 0;
      });
    } else return [];
  }, [
    priceReductionType,
    pricingType,
    reducedPriceExists,
    triplePricePartitions,
    tripleRoomOptions,
  ]);

  return fields && fields.length ? (
    <div className="flex flex-col gap-4">
      {fields.map((field, indexField) => (
        <HotelRoomRepartitionForm
          key={field.id}
          control={control}
          index={index}
          tarifIndex={tarifIndex}
          indexField={indexField}
          pricingType={pricingType}
          reducedPriceExists={reducedPriceExists}
          priceReductionType={priceReductionType}
          childAges={childAges}
          combination={TRIPLE_ROOM_COMBINATIONS[tripleRoomOptions[indexField]]}
          totals={totals}
          type="triple"
          isViewMode={isViewMode}
          showTotal={pricingType === 'byPerson'}
          defaultPrices={{
            defaultAdultPrice: defaultValues.price,
            defaultFirstSpecialPrice: defaultValues.firstSpecialPrice,
            defaultSecondSpecialPrice: defaultValues.secondSpecialPrice,
          }}
        />
      ))}
    </div>
  ) : null;
};

export const QuadrupleHotelRoomPriceRepartitionList = ({
  control,
  index,
  tarifIndex,
  pricingType,
  reducedPriceExists,
  priceReductionType,
  isViewMode = false,
}: HotelRoomPriceRepartitionListProps) => {
  const { getValues } = useFormContext<HotelFormDataWithTranslations>();
  const lastMergedRoomOptionsRef = useRef<number[] | null>(null);

  const { fields, replace } = useFieldArray({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.quadruple`,
  });

  const quadrupleRoomType = useWatch({
    control,
    name: `hotelRoomCategories.${index}.roomTypes.quadruple`,
  });

  const quadrupleRoomOptions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.quadrupleRoomOptions`,
  });

  const childAges = useWatch({ control, name: 'childAges' });

  const defaultPricesExist = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPricesExist`,
  });

  const defaultPrices = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices`,
  });

  const defaultValues = useMemo(
    () => ({
      price:
        defaultPricesExist === 'true' && defaultPrices?.price
          ? Number(defaultPrices?.price)
          : undefined,
      firstSpecialPrice:
        defaultPricesExist === 'true' &&
        pricingType === 'byPerson' &&
        defaultPrices?.firstSpecialPrice
          ? Number(defaultPrices?.firstSpecialPrice)
          : undefined,
      secondSpecialPrice:
        defaultPricesExist === 'true' &&
        pricingType === 'byPerson' &&
        defaultPrices?.secondSpecialPrice
          ? Number(defaultPrices?.secondSpecialPrice)
          : undefined,
    }),
    [
      defaultPrices?.firstSpecialPrice,
      defaultPrices?.price,
      defaultPrices?.secondSpecialPrice,
      defaultPricesExist,
      pricingType,
    ],
  );

  useEffect(() => {
    const path =
      `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.quadruple` as const;
    const opts = quadrupleRoomOptions ?? [];

    if (!quadrupleRoomType) {
      replace([]);
      lastMergedRoomOptionsRef.current = null;
      return;
    }

    const parts = (getValues(path) ??
      []) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['quadruple'];
    const previousRef = lastMergedRoomOptionsRef.current;
    const prevOpts = previousRef ?? opts;

    const merged = mergeHotelPricePartitionsByRoomOptions({
      previousRoomOptionIndices: prevOpts,
      previousPartitions: parts,
      nextRoomOptionIndices: opts,
      createDefaultPartition: () =>
        ({
          price: defaultValues.price,
          firstSpecialPrice: defaultValues.firstSpecialPrice,
          secondSpecialPrice: defaultValues.secondSpecialPrice,
        }) as HotelFormData['hotelRoomCategories'][number]['tarifs'][number]['pricePartitions']['quadruple'][number],
    });

    const optionsDidNotChange =
      prevOpts.length === opts.length &&
      prevOpts.every((value, i) => value === opts[i]);
    if (parts.length === 0 && optionsDidNotChange && previousRef !== null) {
      return;
    }

    if (hotelPricePartitionArraysEqual(merged, parts)) {
      lastMergedRoomOptionsRef.current = [...opts];
      return;
    }

    replace(merged);
    lastMergedRoomOptionsRef.current = [...opts];
  }, [
    defaultValues.firstSpecialPrice,
    defaultValues.price,
    defaultValues.secondSpecialPrice,
    getValues,
    index,
    quadrupleRoomOptions,
    quadrupleRoomType,
    replace,
    tarifIndex,
  ]);

  const quadruplePricePartitions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.quadruple`,
  });

  const totals = useMemo(() => {
    if (quadruplePricePartitions) {
      return quadruplePricePartitions.map((partition, indexPartition) => {
        if (!partition.price) return 0;
        if (pricingType === 'byRoom') return 4 * partition.price;
        else if (pricingType === 'byPerson') {
          if (reducedPriceExists === 'false') {
            return 4 * partition.price;
          } else if (reducedPriceExists === 'true') {
            if (priceReductionType === 'newPrice') {
              let price = 0;
              const currentPartition =
                QUADRUPLE_ROOM_COMBINATIONS[
                  quadrupleRoomOptions[indexPartition]
                ];
              if (!currentPartition || currentPartition.length === 0) return 0;
              price = currentPartition[0] * partition.price;
              if (currentPartition[1] > 0) {
                price +=
                  currentPartition[1] * (partition.firstSpecialPrice ?? 0);
              }
              if (currentPartition[2] > 0) {
                price +=
                  currentPartition[2] * (partition.secondSpecialPrice ?? 0);
              }
              return price;
            } else if (priceReductionType === 'reduction') {
              const currentPartition =
                QUADRUPLE_ROOM_COMBINATIONS[
                  quadrupleRoomOptions[indexPartition]
                ];
              if (!currentPartition || currentPartition.length === 0) return 0;
              let price = 4 * partition.price;
              if (currentPartition && currentPartition[1] > 0) {
                price -=
                  currentPartition[1] * (partition.firstSpecialPrice ?? 0);
              }
              if (currentPartition && currentPartition[2] > 0) {
                price -=
                  currentPartition[2] * (partition.secondSpecialPrice ?? 0);
              }
              return price;
            }
          }
        }
        return 0;
      });
    } else return [];
  }, [
    priceReductionType,
    pricingType,
    reducedPriceExists,
    quadruplePricePartitions,
    quadrupleRoomOptions,
  ]);

  return fields && fields.length ? (
    <div className="flex flex-col gap-4">
      {fields.map((field, indexField) => (
        <HotelRoomRepartitionForm
          key={field.id}
          control={control}
          index={index}
          tarifIndex={tarifIndex}
          indexField={indexField}
          pricingType={pricingType}
          reducedPriceExists={reducedPriceExists}
          priceReductionType={priceReductionType}
          childAges={childAges}
          combination={
            QUADRUPLE_ROOM_COMBINATIONS[quadrupleRoomOptions[indexField]]
          }
          totals={totals}
          type="quadruple"
          isViewMode={isViewMode}
          showTotal={pricingType === 'byPerson'}
          defaultPrices={{
            defaultAdultPrice: defaultValues.price,
            defaultFirstSpecialPrice: defaultValues.firstSpecialPrice,
            defaultSecondSpecialPrice: defaultValues.secondSpecialPrice,
          }}
        />
      ))}
    </div>
  ) : null;
};

interface HotelRoomPartitionFormProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  tarifIndex: number;
  indexField: number;
  pricingType?: 'byPerson' | 'byRoom';
  reducedPriceExists?: 'true' | 'false';
  priceReductionType?: 'newPrice' | 'reduction';
  childAges?: {
    childAgeFrom?: number | undefined;
    childAgeTo?: number | undefined;
  }[];
  combination?: number[];
  totals?: number[];
  type?: 'double' | 'triple' | 'quadruple';
  showTotal?: boolean;
  isViewMode?: boolean;
  defaultPrices?: {
    defaultAdultPrice?: number;
    defaultFirstSpecialPrice?: number;
    defaultSecondSpecialPrice?: number;
  };
}

const HotelRoomRepartitionForm = ({
  control,
  index,
  tarifIndex,
  indexField,
  pricingType,
  reducedPriceExists,
  priceReductionType,
  childAges,
  combination,
  totals,
  type = 'double',
  showTotal = false,
  isViewMode = false,
  defaultPrices,
}: HotelRoomPartitionFormProps) => {
  const { t } = useTranslation();
  const roomOptions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.${type}RoomOptions`,
  });
  const rowPartition = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.${type}.${indexField}`,
  });

  const resolvedDisplayedTotal = useMemo(() => {
    if (totals && totals.length > indexField) return totals[indexField];
    if (!showTotal) return 0;
    const adultPrice = Number(rowPartition?.price ?? 0);
    if (!adultPrice || !combination) return 0;
    if (pricingType === 'byRoom')
      return combination.reduce((a, b) => a + b, 0) * adultPrice;
    if (reducedPriceExists === 'false') {
      return combination.reduce((a, b) => a + b, 0) * adultPrice;
    }
    if (priceReductionType === 'newPrice') {
      return (
        combination[0] * adultPrice +
        combination[1] * Number(rowPartition?.firstSpecialPrice ?? 0) +
        combination[2] * Number(rowPartition?.secondSpecialPrice ?? 0)
      );
    }
    return (
      combination.reduce((a, b) => a + b, 0) * adultPrice -
      combination[1] * Number(rowPartition?.firstSpecialPrice ?? 0) -
      combination[2] * Number(rowPartition?.secondSpecialPrice ?? 0)
    );
  }, [
    totals,
    indexField,
    showTotal,
    rowPartition?.price,
    rowPartition?.firstSpecialPrice,
    rowPartition?.secondSpecialPrice,
    combination,
    pricingType,
    reducedPriceExists,
    priceReductionType,
  ]);

  const roomPartitions = getHotelRoomRepartitionLabel(
    combination ?? [],
    childAges ?? [],
  ).split(' & ');
  if (
    combination?.length === 3 &&
    roomPartitions.length === 2 &&
    combination[1] !== 0
  ) {
    roomPartitions.push(roomPartitions[1]);
    roomPartitions[1] = '';
  }
  return (
    <table>
      <caption className="font-semibold rounded-t-lg pt-2 bg-[#F7F7F7]">
        <ExpertLineBadge
          roomType={type}
          optionIndex={roomOptions?.[indexField]}
        />
        {t(`hotels.roomCategory.roomTypes.${type}`)}
      </caption>
      <thead className="rounded-b-lg bg-[#F7F7F7]">
        <tr>
          <th className="pl-1 pb-2">
            <img src={`/images/${type}_bedroom.png`} className="size-10" />
          </th>
          <th className="text-left w-36">
            {roomPartitions.length > 0 ? roomPartitions[0] : ''}
          </th>
          <th className="text-left w-36">
            {roomPartitions.length > 1 ? roomPartitions[1] : ''}
          </th>
          <th className="text-left w-36">
            {roomPartitions.length > 2 ? roomPartitions[2] : ''}
          </th>
          <th colSpan={2}></th>
        </tr>
      </thead>
      <tbody>
        <tr className="align-top">
          <td></td>
          <td>
            <div className="w-28">
              <StyledInput
                control={control}
                name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.${type}.${indexField}.price`}
                type="number"
                min={0}
                title={
                  pricingType === 'byRoom'
                    ? t('hotels.roomTarifs.totalRoom')
                    : t('hotels.roomTarifs.adultRate')
                }
                className="items-center"
                disabled={isViewMode}
                hideNumberFieldArrows
                compareValue={{
                  value: defaultPrices?.defaultAdultPrice?.toString(),
                  color: 'purple',
                }}
              />
            </div>
          </td>
          <td>
            {pricingType === 'byPerson' &&
              reducedPriceExists === 'true' &&
              combination &&
              combination[2] > 0 && (
                <div className="w-28">
                  <StyledInput
                    control={control}
                    name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.${type}.${indexField}.secondSpecialPrice`}
                    type="number"
                    min={0}
                    title={
                      priceReductionType === 'newPrice'
                        ? t('hotels.roomTarifs.childRate')
                        : t('hotels.roomTarifs.childReduction')
                    }
                    titleClassName={
                      priceReductionType === 'reduction'
                        ? 'text-success-700'
                        : undefined
                    }
                    className="items-center"
                    disabled={isViewMode}
                    hideNumberFieldArrows
                    compareValue={
                      childAges && childAges.length === 2
                        ? {
                            value:
                              defaultPrices?.defaultSecondSpecialPrice?.toString(),
                            color: 'success',
                          }
                        : {
                            value:
                              defaultPrices?.defaultFirstSpecialPrice?.toString(),
                            color: 'warning',
                          }
                    }
                  />
                </div>
              )}
          </td>
          <td>
            {pricingType === 'byPerson' &&
              reducedPriceExists === 'true' &&
              combination &&
              combination[1] > 0 && (
                <div className="w-28">
                  <StyledInput
                    control={control}
                    name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.${type}.${indexField}.firstSpecialPrice`}
                    type="number"
                    min={0}
                    title={
                      priceReductionType === 'newPrice'
                        ? t('hotels.roomTarifs.childRate')
                        : t('hotels.roomTarifs.childReduction')
                    }
                    titleClassName={
                      priceReductionType === 'reduction'
                        ? 'text-success-700'
                        : undefined
                    }
                    className="items-center"
                    disabled={isViewMode}
                    hideNumberFieldArrows
                    compareValue={{
                      value:
                        defaultPrices?.defaultFirstSpecialPrice?.toString(),
                      color: 'warning',
                    }}
                  />
                </div>
              )}
          </td>
          <td>
            {showTotal && (
              <div className="w-24">
                <div className="flex flex-col w-full gap-1.5 items-center">
                  <div className="flex justify-between">
                    <Label className="text-gray-700">
                      {t('hotels.roomTarifs.totalRoomLabel')}
                    </Label>
                  </div>
                  <div className="relative flex items-center">
                    <Input
                      value={resolvedDisplayedTotal}
                      disabled
                      type="number"
                      className="bg-gray-100 hover:cursor-default [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <span className="text-sm leading-6 font-medium text-error-500"></span>
                </div>
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
};
